import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { Show } from '@liveforge/shared';
import { Button } from '../components/ui/button';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { Field, FormError } from '../components/ui/field';
import { Modal } from '../components/ui/modal';
import { InlineLoader } from '../components/ui/spinner';
import { useShows } from '../hooks/use-shows';
import { errorMessage } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { createShow, deleteShow } from '../lib/resources';

export function DashboardPage() {
  const { shows, loading, error, setShows } = useShows();

  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [pendingDelete, setPendingDelete] = useState<Show | null>(null);
  const [deleting, setDeleting] = useState(false);

  function openCreate() {
    setName('');
    setDescription('');
    setFormError(null);
    setCreating(true);
  }

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const show = await createShow({
        name: name.trim(),
        description: description.trim() || null,
      });
      setShows([show, ...shows]);
      setCreating(false);
    } catch (cause) {
      setFormError(errorMessage(cause));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!pendingDelete) {
      return;
    }
    setDeleting(true);
    try {
      await deleteShow(pendingDelete.id);
      setShows(shows.filter((show) => show.id !== pendingDelete.id));
      setPendingDelete(null);
    } catch (cause) {
      setFormError(errorMessage(cause));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Shows</h1>
          <p className="muted">
            A Show is a reusable programme template. Every Live inherits its scenes and branding.
          </p>
        </div>
        <Button variant="primary" onClick={openCreate}>
          New show
        </Button>
      </div>

      {error ? <FormError>{error}</FormError> : null}

      {loading ? (
        <InlineLoader label="Loading shows..." />
      ) : shows.length === 0 ? (
        <div className="empty-state">
          <h2>No shows yet</h2>
          <p className="muted">Create your first Show to start scheduling Lives.</p>
          <Button variant="primary" onClick={openCreate}>
            New show
          </Button>
        </div>
      ) : (
        <div className="card-grid">
          {shows.map((show) => (
            <article className="card show-card" key={show.id}>
              <h2 className="show-card__name">
                <Link to={`/shows/${show.id}`}>{show.name}</Link>
              </h2>
              <p className="show-card__description muted">
                {show.description ?? 'No description.'}
              </p>
              <div className="show-card__footer row row--between">
                <span className="muted">Updated {formatDateTime(show.updatedAt)}</span>
                <Button variant="ghost" small onClick={() => setPendingDelete(show)}>
                  Delete
                </Button>
              </div>
            </article>
          ))}
        </div>
      )}

      <Modal
        open={creating}
        title="New show"
        onClose={() => setCreating(false)}
        actions={
          <>
            <Button variant="secondary" onClick={() => setCreating(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              form="create-show-form"
              type="submit"
              loading={submitting}
              disabled={!name.trim()}
            >
              Create
            </Button>
          </>
        }
      >
        <form id="create-show-form" className="stack" onSubmit={handleCreate}>
          <FormError>{formError}</FormError>
          <Field label="Name">
            {(id) => (
              <input
                id={id}
                className="input"
                type="text"
                required
                autoFocus
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            )}
          </Field>
          <Field label="Description" hint="Optional.">
            {(id) => (
              <textarea
                id={id}
                className="textarea"
                rows={3}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            )}
          </Field>
        </form>
      </Modal>

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Delete show"
        message={
          pendingDelete
            ? `Delete "${pendingDelete.name}"? Every Live created from it is deleted too. This cannot be undone.`
            : ''
        }
        busy={deleting}
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
}
